import React, { ButtonHTMLAttributes } from "react";
import { SbBlokData } from "@storyblok/react";
import clsx from "clsx";
import LoaderOnButton from "../../common/LoaderOnButton";
import { IconV2 } from "../IconV2/IconV2";

type ButtonVariant = "primary" | "secondary" | "tertiary" | "icon" | "text";
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  blok?: {
    variant: ButtonVariant;
    text: string;
    leadingIcon?: SbBlokData[];
    trailingIcon?: (SbBlokData & {
      alt: string;
      height: number;
      icon: string;
      width: number;
    })[];
    isFullWidthOnMobile: boolean;
  };
  onClick: (e: {}) => void;
  isLoading?: boolean;
  additionalButtonClassName?: string;
  fullWidth?: boolean;
  isDisabled?: boolean;
}

export function ButtonV2({
  blok,
  onClick,
  isLoading = false,
  additionalButtonClassName,
  fullWidth,
  isDisabled = false,
  children,
  ...rest
}: ButtonProps) {
  const buttonDynamicClassName = clsx(
    {
      "text-[14px] md:text-base rounded-tr-none rounded-lg font-semibold hover:bg-none flex gap-1 items-center": true,
      "bg-primary text-white dark:text-gray-800 hover:brightness-90 px-4 py-2 md:px-8 md:py-3":
        blok?.variant === "primary",
      "bg-inherit border border-gray-800 text-black px-4 py-2.5 md:px-8": blok?.variant === "secondary",
      "text-primary hover:brightness-70 dark:text-white px-4 py-2 md:px-8 md:py-3": blok?.variant === "tertiary",
      "font-base bg-white hover:bg-none dark:bg-gray-900 dark:text-primary": blok?.variant === "text",
      "flex items-center justify-center rounded-full hover:bg-gray-50 dark:hover:bg-neutral-600 border-0 px-3 py-2":
        blok?.variant === "icon",
      "w-full": fullWidth,
      "w-full sm:w-fit": blok?.isFullWidthOnMobile,
      "opacity-50 cursor-not-allowed": isDisabled || isLoading
    },
    additionalButtonClassName
  );

  return (
    <button disabled={isDisabled} className={buttonDynamicClassName} onClick={onClick} type="button" {...rest}>
      {isLoading ? <LoaderOnButton /> : blok?.text}
      {children}
      {blok?.trailingIcon && blok.trailingIcon.map((nestedBlok) => <IconV2 blok={nestedBlok} key={nestedBlok._uid} />)}
    </button>
  );
}

ButtonV2.defaultProps = {
  fullWidth: false,
  isDisabled: false,
  isLoading: false,
  additionalButtonClassName: ""
};
